<template>

<com-item :name="'game.tracking.' + metric" :size="size" :loading="loading" :is-empty="!active" :title="title">

	<template v-slot:about>

		<p>{{ description }}</p>

	</template>

	<template v-slot:stats>

		<app-visualisation-bars :data="graph" v-if="active" />

	</template>

</com-item>

</template>

<script>

import comItem from './../common/Item'

export default {

	props: ['fetching', 'your', 'you', 'size', 'metric'],

	components: {
		'com-item': comItem
	},

	data: function() {

		return {
			graph: {
				datasets: [],
				axis: {
					x: { labels: [] },
					y: { min: 10, points: 10 }
				}
			}
		}

	},

	computed: {

		loading: function() { return this.fetching === 'all' },

		data: function() { return this.$store.getters['page/insights/game/peers'] },

		history: function() { return (this.data && this.data.history) ? this.data.history : [] },

		currentValue: function() {

			if (!this.data) return null

			var map = {
				owners: this.data.owners,
				wishlisted: this.data.wishlisted,
				totalPlays: this.data.totalPlays,
				players: this.data.totalPlayers,
				avgRating: this.data.avgRating,
				rank: null
			}

			return map[this.metric] !== undefined ? map[this.metric] : null

		},

		points: function() {

			var months = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
			var metric = this.metric
			var cfg = this.config
			var pts = []

			for (var i = 0; i < this.history.length; i++) {

				var item = this.history[i]
				var date = new Date(item.date * 1000)

				var point = {
					value: item[metric] || 0,
					label: months[date.getMonth() + 1] + ' ' + date.getFullYear()
				}

				// Attach monthly activity fields from history
				if (cfg.barAdd) point.barAdd = item[cfg.barAdd] || 0
				if (cfg.barRemove) point.barRemove = item[cfg.barRemove] || 0
				if (cfg.barDelta) point.barDelta = item[cfg.barDelta] || 0

				pts.push(point)

			}

			if (this.currentValue !== null) {

				var now = new Date()
				var currentLabel = months[now.getMonth() + 1] + ' ' + now.getFullYear()

				if (pts.length && pts[pts.length - 1].label === currentLabel) {

					pts[pts.length - 1].value = this.currentValue

				} else {

					pts.push({ value: this.currentValue, label: currentLabel, barAdd: 0, barRemove: 0, barDelta: 0 })

				}

			}

			return pts

		},

		active: function() { return this.points.length > 0 && !this.loading },

		config: function() {

			var suffix = ''
			if (this.history.length) {
				var months = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
				var first = new Date(this.history[0].date * 1000)
				suffix = ' Tracking started ' + months[first.getMonth() + 1] + ' ' + first.getFullYear() + '.'
			}

			var configs = {
				owners: {
					title: 'Owners',
					description: 'Ownership over time.' + suffix,
					tooltip: '{{value}} owners <small>{{x.label}}</small>',
					bars: 'split',
					barAdd: 'ownersAdded',
					barRemove: 'ownersRemoved',
					barAddLabel: 'added',
					barRemoveLabel: 'removed'
				},
				wishlisted: {
					title: 'Wishlisted',
					description: 'Wishlist count over time.' + suffix,
					tooltip: '{{value}} wishlisted <small>{{x.label}}</small>',
					bars: 'split',
					barAdd: 'wishAdded',
					barRemove: 'wishRemoved',
					barAddLabel: 'added',
					barRemoveLabel: 'removed'
				},
				totalPlays: {
					title: 'Total Plays',
					description: 'Cumulative plays over time.' + suffix,
					tooltip: '{{value}} plays <small>{{x.label}}</small>',
					bars: 'delta',
					barDelta: 'playsMonth',
					barLabel: 'this month'
				},
				players: {
					title: 'Players',
					description: 'Unique players over time.' + suffix,
					tooltip: '{{value}} players <small>{{x.label}}</small>',
					bars: 'delta',
					barDelta: 'playersNew',
					barLabel: 'new'
				},
				avgRating: {
					title: 'Avg Rating',
					description: 'Community rating over time.' + suffix,
					tooltip: '{{value}} avg <small>{{x.label}}</small>',
					bars: 'delta',
					barDelta: 'ratingsAdded',
					barLabel: 'new ratings'
				},
				rank: {
					title: 'BGG Rank',
					description: 'BGG rank over time.' + suffix,
					tooltip: '#{{value}} <small>{{x.label}}</small>',
					bars: false
				}
			}

			return configs[this.metric] || {}

		},

		title: function() { return this.config.title || '' },

		description: function() { return this.config.description || '' }

	},

	created: function() { this.visualise() },

	watch: { data: function() { this.visualise() } },

	methods: {

		visualise: function() {

			if (!this.points.length) return

			var cfg = this.config
			var lineData = []
			var labels = []

			for (var i = 0; i < this.points.length; i++) {

				lineData.push(this.points[i].value)
				labels.push({ label: this.points[i].label })

			}

			var datasets = [
				{
					type: 'point',
					data: lineData,
					backgroundColor: '#4881bb',
					shadeColor: 'rgba(72, 129, 187, 0.1)',
					lineColor: 'rgba(72, 129, 187, 0.4)',
					tooltip: cfg.tooltip || ''
				}
			]

			if (cfg.bars && this.points.length > 0) {

				var hasBars = false

				if (cfg.bars === 'delta') {

					var deltaData = []

					for (var d = 0; d < this.points.length; d++) {

						var val = this.points[d].barDelta || 0
						deltaData.push(val)
						if (val > 0) hasBars = true

					}

					if (hasBars) {

						datasets.push({
							type: 'bar',
							data: deltaData,
							backgroundColor: 'rgba(47, 196, 130, 0.5)',
							yAxis: 'right',
							tooltip: '{{value}} ' + cfg.barLabel + ' <small>{{x.label}}</small>'
						})

					}

				} else if (cfg.bars === 'split') {

					var addData = []
					var removeData = []

					for (var s = 0; s < this.points.length; s++) {

						var a = this.points[s].barAdd || 0
						var r = this.points[s].barRemove || 0
						addData.push(a)
						removeData.push(r)
						if (a > 0 || r > 0) hasBars = true

					}

					if (hasBars) {

						datasets.push({
							type: 'bar',
							data: addData,
							backgroundColor: 'rgba(47, 196, 130, 0.5)',
							yAxis: 'right',
							tooltip: '{{value}} ' + cfg.barAddLabel + ' <small>{{x.label}}</small>'
						})

						datasets.push({
							type: 'bar',
							data: removeData,
							backgroundColor: 'rgba(192, 57, 43, 0.5)',
							yAxis: 'right',
							tooltip: '{{value}} ' + cfg.barRemoveLabel + ' <small>{{x.label}}</small>'
						})

					}

				}

				if (hasBars) {

					this.graph.axis.y.right = { min: 10, points: 5 }

				} else {

					delete this.graph.axis.y.right

				}

			} else {

				delete this.graph.axis.y.right

			}

			this.graph.datasets = datasets
			this.graph.axis.x.labels = labels

		}

	}

}

</script>

<style scoped>
</style>
